/**
 * WeekContext
 * Global context for managing the current week across the entire application
 * Synced with URL query parameter for persistence across refreshes and navigation
 */

import { createContext, useContext, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import { useSearch, useNavigate } from '@tanstack/react-router';
import {
  getWeekStart,
  getNextWeek,
  getPreviousWeek,
  formatDateForAPI,
  parseDateFromAPI,
  isValidDate,
} from '@/lib/utils/date';
import type { RootSearchParams } from '@/routes/__root';

interface WeekContextValue {
  /** Current week start date (Monday) */
  weekStart: Date;
  /** Current week start date in API format (YYYY-MM-DD) */
  weekStartDate: string;
  /** Navigate to previous week */
  goToPreviousWeek: () => void;
  /** Navigate to next week */
  goToNextWeek: () => void;
  /** Navigate to current week (today) */
  goToToday: () => void;
  /** Set week to a specific date */
  setWeekStart: (date: Date) => void;
}

const WeekContext = createContext<WeekContextValue | undefined>(undefined);

interface WeekProviderProps {
  children: ReactNode;
}

/**
 * WeekProvider component
 * Provides global week management for the entire application
 * Syncs week state with URL query parameter for persistence
 *
 * @example
 * ```tsx
 * <WeekProvider>
 *   <App />
 * </WeekProvider>
 * ```
 */
export function WeekProvider({ children }: WeekProviderProps) {
  // Read week from URL query parameter
  const search = useSearch({ from: '__root__' }) as RootSearchParams;
  const navigate = useNavigate();

  // Derive weekStart from URL or default to current week
  const weekStart = useMemo(() => {
    if (search.week && isValidDate(search.week)) {
      return getWeekStart(parseDateFromAPI(search.week));
    }
    return getWeekStart(new Date());
  }, [search.week]);

  const weekStartDate = formatDateForAPI(weekStart);

  // Helper to update the URL with a new week
  const updateWeekInUrl = useCallback(
    (newWeekStart: Date) => {
      const newWeekDate = formatDateForAPI(newWeekStart);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      navigate({
        search: {
          ...search,
          week: newWeekDate,
        } as any,
        replace: true, // Use replace to avoid cluttering browser history
      });
    },
    [navigate, search]
  );

  const goToPreviousWeek = useCallback(() => {
    updateWeekInUrl(getPreviousWeek(weekStart));
  }, [weekStart, updateWeekInUrl]);

  const goToNextWeek = useCallback(() => {
    updateWeekInUrl(getNextWeek(weekStart));
  }, [weekStart, updateWeekInUrl]);

  const goToToday = useCallback(() => {
    const today = getWeekStart(new Date());
    updateWeekInUrl(today);
  }, [updateWeekInUrl]);

  const setWeekStart = useCallback(
    (date: Date) => {
      updateWeekInUrl(getWeekStart(date));
    },
    [updateWeekInUrl]
  );

  const value: WeekContextValue = {
    weekStart,
    weekStartDate,
    goToPreviousWeek,
    goToNextWeek,
    goToToday,
    setWeekStart,
  };

  return <WeekContext.Provider value={value}>{children}</WeekContext.Provider>;
}

/**
 * Hook to access the week context
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { weekStartDate, goToNextWeek } = useWeek();
 *
 *   return (
 *     <>
 *       <p>Current week: {weekStartDate}</p>
 *       <button onClick={goToNextWeek}>Next Week</button>
 *     </>
 *   );
 * }
 * ```
 */
export function useWeek() {
  const context = useContext(WeekContext);
  if (!context) {
    throw new Error('useWeek must be used within WeekProvider');
  }
  return context;
}
